import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';
import { CalcNetPanel, Row, UnitInput, ResultTable } from '../_fitnessHelpers';

/* Full 3-tab Pace calculator: compute Pace / Time / Distance */

const EVENTS = [
  { name: '5K', km: 5 },
  { name: '10K', km: 10 },
  { name: '15K', km: 15 },
  { name: 'Half marathon', km: 21.0975 },
  { name: 'Marathon', km: 42.195 },
  { name: '100 m', km: 0.1 },
  { name: '200 m', km: 0.2 },
  { name: '400 m', km: 0.4 },
  { name: '800 m', km: 0.8 },
  { name: '1 mile', km: 1.60934 },
];

// Parse "hh:mm:ss" or "mm:ss" or "ss" into seconds
function parseHMS(str) {
  if (str === '' || str == null) return 0;
  const parts = String(str).split(':').map(x => +x || 0);
  if (parts.length === 3) return parts[0]*3600 + parts[1]*60 + parts[2];
  if (parts.length === 2) return parts[0]*60 + parts[1];
  return parts[0];
}
function fmtHMS(totalSec) {
  totalSec = Math.max(0, Math.round(totalSec));
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const p = n => String(n).padStart(2, '0');
  return `${p(h)}:${p(m)}:${p(s)}`;
}

export default function PaceCalc() {
  const [tab, setTab] = useState(0); // 0 Pace, 1 Time, 2 Distance
  const [distance, setDistance] = useState(5);
  const [distUnit, setDistUnit] = useState('mi');
  const [event, setEvent] = useState('');
  const [timeStr, setTimeStr] = useState('00:50:25');
  const [paceStr, setPaceStr] = useState('00:10:05');

  const clear = () => {
    setDistance(5); setDistUnit('mi'); setEvent(''); setTimeStr('00:50:25'); setPaceStr('00:10:05');
  };

  const distanceValue = event ? (distUnit === 'mi' ? EVENTS.find(e=>e.name===event).km/1.60934 : EVENTS.find(e=>e.name===event).km) : (+distance||0);
  const timeSec = parseHMS(timeStr);
  const paceSec = parseHMS(paceStr);

  const result = useMemo(() => {
    if (tab === 0) {
      // Pace = Time / Distance
      const secPerUnit = distanceValue > 0 ? timeSec / distanceValue : 0;
      return { label: `Pace`, value: `${fmtHMS(secPerUnit)} per ${distUnit === 'mi' ? 'mile' : 'km'}` };
    } else if (tab === 1) {
      // Time = Pace × Distance
      const t = paceSec * distanceValue;
      return { label: `Time`, value: fmtHMS(t) };
    } else {
      // Distance = Time / Pace
      const d = paceSec > 0 ? timeSec / paceSec : 0;
      return { label: `Distance`, value: `${d.toFixed(2)} ${distUnit === 'mi' ? 'miles' : 'km'}` };
    }
  }, [tab, timeSec, paceSec, distanceValue, distUnit]);

  return (
    <CalcShell look="cnet" title="Pace Calculator" subtitle="Compute pace, time, or distance for running, walking, or biking.">
      <CalcNetPanel
        slug="pace"
        tabs={['Pace', 'Time', 'Distance']}
        activeTab={tab}
        onTabChange={setTab}
        belowGrid={
          <div className="calc-card p-6">
            <h3 className="font-bold text-slate-900 mb-3">Pace at common race distances</h3>
            <table className="w-full text-sm border-collapse max-w-md">
              <thead><tr>
                <th className="bg-[#1e40af] text-white px-3 py-2 text-left border">Event</th>
                <th className="bg-[#1e40af] text-white px-3 py-2 text-right border">Time at current pace</th>
              </tr></thead>
              <tbody>
                {EVENTS.filter(e => e.km >= 1).map((ev, i) => {
                  const distInUnit = distUnit === 'mi' ? ev.km / 1.60934 : ev.km;
                  const paceUsed = tab === 0 ? (distanceValue > 0 ? timeSec / distanceValue : 0) : paceSec;
                  const t = paceUsed * distInUnit;
                  return <tr key={ev.name} className={i%2?'bg-slate-50':''}><td className="border px-3 py-1.5">{ev.name}</td><td className="border px-3 py-1.5 text-right">{fmtHMS(t)}</td></tr>;
                })}
              </tbody>
            </table>
          </div>
        }
        onClear={clear}
        right={
          <div>
            <div className="text-lg text-slate-800">{result.label}</div>
            <div className="text-2xl font-bold text-[#2f9c3f]" data-testid="pace-result">{result.value}</div>
            <p className="mt-3 text-xs text-slate-600">Placeholder zeros do not need to be entered — "5:3" is treated as 5 min 3 s.</p>
          </div>
        }
      >
        {tab !== 1 && (
          <Row label="Time">
            <div className="relative flex-1">
              <input type="text" value={timeStr} onChange={e => setTimeStr(e.target.value)} data-testid="pace-time"
                className="w-full px-3 py-2 pr-20 bg-white border border-slate-400 rounded-sm text-slate-900 text-sm focus:outline-none focus:border-blue-600" />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none">hh:mm:ss</span>
            </div>
          </Row>
        )}
        {tab !== 0 && (
          <Row label="Pace">
            <div className="relative flex-1">
              <input type="text" value={paceStr} onChange={e => setPaceStr(e.target.value)} data-testid="pace-pace"
                className="w-full px-3 py-2 pr-24 bg-white border border-slate-400 rounded-sm text-slate-900 text-sm focus:outline-none focus:border-blue-600" />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none">per {distUnit === 'mi' ? 'mile' : 'km'}</span>
            </div>
          </Row>
        )}
        {tab !== 2 && (
          <>
            <Row label="Distance">
              <div className="grid grid-cols-2 gap-2 flex-1">
                <UnitInput value={distance} onChange={setDistance} testid="pace-distance" />
                <select value={distUnit} onChange={e => setDistUnit(e.target.value)} data-testid="pace-unit"
                  className="w-full px-3 py-2 bg-white border border-slate-400 rounded-sm text-sm">
                  <option value="mi">mi — mile</option>
                  <option value="km">km — kilometer</option>
                </select>
              </div>
            </Row>
            <Row label="Or pick"><select value={event} onChange={e => setEvent(e.target.value)} data-testid="pace-event"
              className="flex-1 px-3 py-2 bg-white border border-slate-400 rounded-sm text-sm">
              <option value="">-- Or pick an event --</option>
              {EVENTS.map(ev => <option key={ev.name}>{ev.name}</option>)}
            </select></Row>
          </>
        )}
      </CalcNetPanel>

      <StepsPanel steps={[
        `Pace = Time ÷ Distance = ${fmtHMS(timeSec)} ÷ ${distanceValue.toFixed(2)} ${distUnit} = ${fmtHMS(distanceValue>0? timeSec/distanceValue : 0)} per ${distUnit}`,
        `Speed = Distance ÷ Time = ${distanceValue.toFixed(2)} ${distUnit} ÷ ${(timeSec/3600).toFixed(3)} h`,
      ]} />
    </CalcShell>
  );
}
